import type { PriceMolProps } from "@onestore/hel/dist/components/molecules/PriceMol"
import _t from "@core/i18n"
import type { PeriodInfo } from "@core/period-info"
import { CURRENCY_SEPARATOR, getCurrencySign } from "@core/pricing"
import type { PriceType } from "@core/types"
import type { PriceData } from "@ionos-web-design-system/react"
import type { PriceInfo } from "~/fragments/priceInfo"
import isEmpty from "~/lib/isEmpty"

type PostlineItem = {
  content: string
  weight: "bold" | "black" | "normal"
  tooltip?: string
}

export const getAdditionalPriceInformation = (
  priceInfo: PriceInfo,
  currentPriceType: PriceType,
  renewPrice?: string,
  periodType?: number | null,
  periodLength?: number,
  customAdditionalInfo?: string[],
  hidePriceInfoLabels?: boolean
): PostlineItem[] => {
  const additionalInformations: PostlineItem[] = []

  if (
    priceInfo.hasRenewalPriceText &&
    isEmpty(customAdditionalInfo) &&
    !isEmpty(renewPrice)
  ) {
    additionalInformations.push({
      content: `${_t("prices.renewalPrefix")} ${renewPrice} ${_t(`prices.${currentPriceType}`)} ${_t(
        `periodName.renewPrice.${periodType}`,
        {
          smart_count: periodLength,
        }
      )}`,
      weight: "normal",
    })
  }

  if (priceInfo.hasRenewalPriceText && !isEmpty(customAdditionalInfo)) {
    additionalInformations.push(
      ...customAdditionalInfo.map((info) => ({
        content: info,
        weight: "normal" as const,
      }))
    )
  }

  if (!hidePriceInfoLabels && !isEmpty(priceInfo?.labels)) {
    const labels: PostlineItem[] =
      priceInfo?.labels.map((label): PostlineItem => {
        return {
          content: label.flatData.text,
          weight: "normal",
          tooltip: !isEmpty(label.flatData.tooltip)
            ? label.flatData.tooltip
            : undefined,
        }
      }) || []

    additionalInformations.push(...labels)
  }

  return additionalInformations
}

export const getDetailedPriceData = (
  currentPriceType: PriceType,
  periodInfo: PeriodInfo,
  priceInfo?: PriceInfo,
  customRenewPriceTexts?: string[],
  showPriceInfoLabelsOnlyOnDefaultPeriod?: boolean,
  hasNoTopLine?: boolean,
  displaySuffixInAdditionalInfo?: boolean,
  dataTestId?: PriceMolProps["mainPrice"]["dataTest"],
  hasSeparateCurrency?: boolean
): PriceData => {
  const hasPromotion = periodInfo.hasPromotion()
  const isDefaultPeriod = periodInfo.getPeriodObject().default

  const { lowestPrice, renewPrice, promotionPercent } =
    periodInfo.getPrices(currentPriceType)

  const { priceValue, secondPeriodPriceValue } = periodInfo.getPrices(
    currentPriceType,
    hasSeparateCurrency
  )

  let resolvedPriceValue = priceValue
  let priceSuffix = `${_t(`prices.${currentPriceType}`)} ${periodInfo.periodText(false, true)}`

  let renewalDiscountPercent = periodInfo.getRenualDiscountPercent()

  // In case of FIRST_PURCHASE_FREE presentation, the price shown to the user is the second period price,
  // and the price suffix needs to be updated to reflect that.
  if (
    periodInfo.isFirstPurchaseFreePresentation() &&
    !isEmpty(secondPeriodPriceValue)
  ) {
    resolvedPriceValue = secondPeriodPriceValue
    priceSuffix = `${_t(`prices.${currentPriceType}`)} ${periodInfo.periodText(false, false, true)}`
    // TODO [ONESTORE-431] - This is a temporary solution to show the promotion percent for first purchase free presentation. We should get this from api.
    renewalDiscountPercent = promotionPercent
  }

  const additionalPriceInformation = !isEmpty(priceInfo)
    ? getAdditionalPriceInformation(
        priceInfo,
        currentPriceType,
        renewPrice,
        periodInfo.getPeriodType(),
        periodInfo.getPeriodLength(),
        customRenewPriceTexts,
        showPriceInfoLabelsOnlyOnDefaultPeriod && !isDefaultPeriod
      )
    : []

  const discountText =
    renewalDiscountPercent === "0%" ? null : renewalDiscountPercent

  return {
    main: {
      comma: CURRENCY_SEPARATOR,
      currencySymbolPosition: "after",
      ...(hasSeparateCurrency ? { currency: getCurrencySign() } : {}),
      decimalPrice: resolvedPriceValue.split(CURRENCY_SEPARATOR)[1] || "00",
      integerPrice: resolvedPriceValue.split(CURRENCY_SEPARATOR)[0],
      additionalInfo: displaySuffixInAdditionalInfo ? priceSuffix : undefined,
      testId: dataTestId?.currentPriceDataTestId,
    },
    postlines: [
      ...(!displaySuffixInAdditionalInfo
        ? [
            {
              content: priceSuffix,
              weight: "black" as const,
            },
          ]
        : []),
      ...additionalPriceInformation,
      {
        content: hasPromotion
          ? _t("prices.lowestPriceWithType", {
              priceType: _t(`prices.${currentPriceType}`),
            }) +
            " " +
            lowestPrice
          : "",
        weight: "normal" as const,
      },
    ].filter((item) => item.content !== ""),
    prelines: hasNoTopLine
      ? undefined
      : {
          primary: {
            discount: discountText
              ? hasPromotion
                ? `${_t("priceMol.discountLabel")} ${discountText}`
                : `${discountText}`
              : undefined,
            strike: renewPrice,
          },
        },
  }
}
